<template>
  <Dialog :open="open" :title="title" :maxWidth="360" @close="$emit('close')">
    <div v-if="record" class="info" :class="{ 'info--prearm': record.status === 'pre-arm' }">
      <span class="info__icon" v-html="shieldSvg"></span>
      <div class="info__main">
        <div class="info__status">{{ headline }}</div>
        <div class="info__detail">{{ detailText }}</div>
        <div class="info__by">By {{ initiatorLabel }}<span v-if="record.reason"> · {{ record.reason }}</span></div>
      </div>
    </div>
    <div v-else class="info-empty">This camera is active in the alarm system.</div>

    <template #actions>
      <button class="dlg-btn dlg-btn--ghost" @click="$emit('close')">Close</button>
      <button v-if="record && canBypass" class="dlg-btn dlg-btn--clear" @click="onClear">Clear Bypass</button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from '@/components/Dialog'
import { bypassDetailText, bypassInitiatorLabel } from '@/models/bypass'

// Compact single-camera bypass info, shown as a centered dialog (not a full modal)
// when a camera's bypass tag is tapped.
export default {
  name: 'bypass-camera-dialog',
  components: { Dialog },
  props: {
    open: { type: Boolean, default: false },
    deviceId: { type: String, default: null },
    // Ionic modals don't reliably inject $store; allow it to be passed in.
    store: { type: Object, default: null },
  },
  data: function () {
    return {
      shieldSvg: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"/><line x1="8.5" y1="15" x2="15.5" y2="9"/></svg>',
    }
  },
  computed: {
    bpStore: function () {
      return this.store || this.$store
    },
    record: function () {
      if (!this.deviceId) return null
      const r = this.bpStore.getters.bypassRecord(this.deviceId)
      return r && r.status !== 'awaiting' ? r : null
    },
    // Clear Bypass is an action — only for roles with Camera Bypass permission
    // (res 13, mode 2). View-only roles see the status but can't clear it.
    canBypass: function () {
      return this.bpStore.getters.canBypassCameras
    },
    now: function () {
      return this.bpStore.getters.bypassNow
    },
    title: function () {
      const map = {}
      ;(this.bpStore.getters.cameraList || []).forEach(c => {
        map[c.deviceId] = c.name || c.model || 'No name'
      })
      return map[this.deviceId] || 'Camera'
    },
    headline: function () {
      if (!this.record) return ''
      return this.record.status === 'pre-arm' ? 'Pre-Arm bypass' : 'Bypassed'
    },
    detailText: function () {
      return bypassDetailText(this.record, this.now)
    },
    initiatorLabel: function () {
      return bypassInitiatorLabel(this.record)
    },
  },
  methods: {
    onClear: function () {
      this.bpStore.dispatch('clearBypass', { deviceId: this.deviceId })
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 14px;
  background: var(--chekt-bypass-0);
  box-shadow: 0 0 0 1px rgba(245, 166, 35, 0.35) inset;
}
.info--prearm {
  background: repeating-linear-gradient(45deg, rgba(245, 166, 35, 0.14), rgba(245, 166, 35, 0.14) 6px, rgba(245, 166, 35, 0.07) 6px, rgba(245, 166, 35, 0.07) 12px);
  box-shadow: none;
  border: 1px dashed rgba(245, 166, 35, 0.7);
}
.info__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  color: #ffffff;
  background: var(--chekt-bypass);
}
.info__main {
  flex: 1;
  min-width: 0;
}
.info__status {
  font-size: 16px;
  font-weight: 700;
  color: var(--chekt-bypass-8);
}
.info__detail {
  margin-top: 3px;
  font-size: 14px;
  color: var(--chekt-bypass-7);
  font-variant-numeric: tabular-nums;
}
.info__by {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.6;
}
.info-empty {
  padding: 8px 2px;
  font-size: 14px;
  opacity: 0.7;
}
.dlg-btn {
  flex: 1;
  padding: 11px 0;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.15s ease;
}
.dlg-btn--ghost {
  background: transparent;
  border-color: rgba(0, 0, 0, 0.22);
  color: var(--ion-text-color);
}
.dlg-btn--clear {
  background: var(--chekt-bypass);
  color: #ffffff;
}
</style>
